import { client } from "@/client";
import { CustomToastComponent } from "@/components/shared";
import { RemoveConfigAdministratorDocument, RemoveConfigAdministratorMutation, RemoveConfigAdministratorMutationVariables } from "@/graphql/__generated__/graphql";
import { useMutation } from "@apollo/client/react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";

/**
 * Custom hook for removing a config administrator.
 * @returns {Object}
 */
export default function useRemoveConfigAdministratorMutationHook(): { removeConfigAdministratorMutation: any, loading: any, error: any} {

  const { t } = useTranslation(["toasts"]);

  const [removeConfigAdministratorMutation, { loading, error }] = useMutation<RemoveConfigAdministratorMutation, RemoveConfigAdministratorMutationVariables>(
    RemoveConfigAdministratorDocument, {
      onCompleted: (data) => {
        toast(CustomToastComponent, {
          toastId: "config-administrator-removed-success",
          data: {
            title: t("toasts.configadministratorremoved"),
            content: t("toasts.configadministratorremovedsuccessfully"),
            type: "success",
          }
        });

        // Remove the user from the configAdministrators query cache
        updatePaginatedCache(data.removeConfigAdministrator);
      }
    });

  /**
   * Updates the paginated config administrators cache by removing the specified administrator.
   * @param {number} removedUserId - The ID of the user to be removed from the cache.
   */
  const updatePaginatedCache = (removedUserId: number) => {
    // Use cache.modify to update the configAdministrators query on ROOT_QUERY
    client.cache.modify({
      fields: {
        paginatedConfigAdministrators(existingData, { readField }) {
          if (!existingData?.edges) {
            return existingData;
          }

          // Filter out the removed administrator from the edges
          const filteredEdges = existingData.edges.filter((edge: any) => {
            // Use readField to get the id in a cache-agnostic way
            const nodeId = readField("id", edge.node);

            return String(nodeId) !== String(removedUserId);
          });

          return {
            ...existingData,
            edges: filteredEdges
          };
        }
      }
    });
  };

  return {
    removeConfigAdministratorMutation,
    loading,
    error
  };
}
